import type { SocialAuthProvider } from "@pax/api-contract/client-config";
import { focusHalo, HapticSurface, panelMotion, press } from "@pax/ui";
import { ProviderMark } from "./provider-marks";

const PROVIDER_LABELS: Record<SocialAuthProvider, string> = {
  google: "Continue with Google",
  apple: "Continue with Apple",
  wechat: "Continue with WeChat",
};

/**
 * The mark sits on the left edge and the label stays in the middle of the
 * button, so a column of these reads as one list of names rather than three
 * lines of text starting at three different places.
 */
const row = `relative flex h-12 w-full items-center justify-center rounded-full px-12 text-sm font-medium disabled:opacity-50 ${panelMotion} ${press} ${focusHalo}`;

const lead = `bg-foreground text-background shadow-[0_1px_2px_rgb(0_0_0/0.1),0_10px_28px_-12px_rgb(0_0_0/0.35)] hover:bg-foreground/90 ${row}`;

const rest = `bg-foreground/[0.06] text-foreground hover:bg-foreground/[0.1] ${row}`;

/**
 * The ways in, in the order the API lists them.
 *
 * The first is the one the page leads with — solid ink against the two soft
 * ones below it — because a screen where every option looks equally likely
 * leaves the choosing to the reader, and most of them are here for the same
 * provider.
 */
export function SocialAuthButtons({
  providers,
  onSelect,
}: {
  providers: SocialAuthProvider[];
  onSelect: (provider: SocialAuthProvider) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {providers.map((provider, index) => (
        <button
          key={provider}
          type="button"
          onClick={() => onSelect(provider)}
          className={index === 0 ? lead : rest}
        >
          <span className="absolute left-5 flex items-center">
            <ProviderMark provider={provider} />
          </span>
          {PROVIDER_LABELS[provider]}
          <HapticSurface />
        </button>
      ))}
    </div>
  );
}
